import ctl from '@netlify/classnames-template-literals'
import { AnimatePresence, motion, useAnimationControls, useDragControls } from 'motion/react'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { createHtmlPortalNode, InPortal, OutPortal } from 'react-reverse-portal'
import { AnimateChangeInHeight } from '../../animation'
import { Glyphs } from '../../glyphs'
import { Tooltip } from '../../tooltip'

export interface WidgetTabsProps<T extends string> {
	hidden?: boolean

	name?: ReactNode

	value: T
	onChange: (value: T) => void

	tabs: Array<{
		id: T
		label: string
		children: ReactNode
	}>

	/** See `animate` on `AnimateChangeInHeightProps` — set false in a shared scroll column. */
	animateHeight?: boolean
}

export function TabbedWidgetWrapper<T extends string>(
	{ hidden, value, onChange, tabs, name, animateHeight = true }: WidgetTabsProps<T>,
) {
	const [isFloating, setIsFloating] = useState(false)
	const [floatingPosition, setFloatingPosition] = useState<
		{ x: number; y: number; width: number } | null
	>(null)

	const wrapperRef = useRef<HTMLDivElement>(null)
	const dragControls = useDragControls()
	const animationControls = useAnimationControls()

	const handlePopOut = (e: React.PointerEvent<HTMLButtonElement>) => {
		const rect = wrapperRef.current?.getBoundingClientRect()
		if (!rect) { return }

		animationControls.set({
			x: rect.left,
			y: rect.top,
			width: rect.width,
		})
		setIsFloating(true)
	}

	const handlePopIn = () => {
		animationControls.set({
			x: 0,
			y: 0,
			width: 'auto',
		})
		setIsFloating(false)
		setFloatingPosition(null)
	}

	if (hidden) {
		return null
	}

	const portalNode = useMemo(() => createHtmlPortalNode(), [])

	return (
		<>
			<InPortal node={portalNode}>
				<InnerFloatingPanel
					hidden={hidden}
					value={value}
					onChange={onChange}
					tabs={tabs}
					onPopOut={handlePopOut}
					onPopIn={handlePopIn}
					dragControls={dragControls}
					animationControls={animationControls}
					isFloating={isFloating}
					name={name}
					animateHeight={animateHeight}
				/>
			</InPortal>

			<AnimateChangeInHeight duration={0.3} hideOverflow={false} animate={animateHeight}>
				{isFloating && (
					<>
						{createPortal(
							<div className="fixed inset-0 pointer-events-none">
								<motion.div>
									<OutPortal node={portalNode} />
								</motion.div>
							</div>,
							document.body,
						)}
					</>
				)}

				{!isFloating && (
					<motion.div ref={wrapperRef}>
						<OutPortal node={portalNode} />
					</motion.div>
				)}
			</AnimateChangeInHeight>
		</>
	)
}

type InnerFloatingPanelProps<T extends string> = WidgetTabsProps<T> & {
	onPopOut: (e: React.PointerEvent<HTMLButtonElement>) => void
	onPopIn: () => void
	dragControls: ReturnType<typeof useDragControls>
	animationControls: ReturnType<typeof useAnimationControls>
	isFloating: boolean
	name?: ReactNode
}

function InnerFloatingPanel<T extends string>(
	{
		hidden,
		value,
		onChange,
		tabs,
		onPopOut,
		onPopIn,
		dragControls,
		animationControls,
		isFloating,
		name,
		animateHeight = true,
	}: InnerFloatingPanelProps<T>,
) {
	const handleToggleFloating = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		e.preventDefault()

		if (isFloating) {
			onPopIn()
		} else {
			onPopOut(e)
		}
	}, [isFloating, onPopIn, onPopOut])

	return (
		<motion.div
			drag
			dragControls={dragControls}
			animate={animationControls}
			dragListener={false}
			dragMomentum={false}
			className={ctl(`
				[contain:layout] text-xs bg-grey-50 ring ring-grey-100 text-grey-700 text-center rounded-sm p-[3px]  w-full
				${isFloating && 'shadow-lg shadow-purple-800/20'}
				${!isFloating && 'shadow'}
				transition-shadow duration-500
				pointer-events-auto
			`)}
		>
			<div className="">
				<AnimatePresence>
					{isFloating && (
						<motion.div
							initial={{ height: 0 }}
							animate={{ height: 'auto' }}
							exit={{ height: 0 }}
							className={ctl(`
								overflow-hidden
							`)}
							onPointerDown={(e) => dragControls.start(e)}
						>
							<div
								className={ctl(`
								grid grid-cols-[1fr_auto_1fr] items-center cursor-grab active:cursor-grabbing
								[background-image:radial-gradient(var(--color-grey-100)_1px,transparent_1px),radial-gradient(var(--color-grey-100)_1px,transparent_1px)]
								[background-size:6px_6px]
								[background-position:0_0,3px_3px]
								user-select-none
							`)}
							>
								<div></div>

								<div className="justify-self-center font-medium">
									{name}
								</div>

								<motion.div
									className="mb-1 mr-[1px] w-[max-content] justify-self-end"
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
								>
									<PopButton
										isFloating={isFloating}
										handleToggleFloating={handleToggleFloating}
										icon={<Glyphs.core.LookDownIcon />}
									/>
								</motion.div>
							</div>
							<div className="border-b border-grey-100 mx-[-4px] h-[1px] mb-[2px]" />
						</motion.div>
					)}
				</AnimatePresence>
				<div className="flex mx-[-1px] gap-1" role="tablist">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							className={ctl(`
								rounded-t-sm py-1.5 px-2.5 border-t border-x border-grey-100 relative
								font-medium cursor-pointer
								${tab.id === value && 'border-b-0 mb-[-1px] bg-white z-10'}
								${
								tab.id !== value
								&& 'border-b bg-gradient-to-b from-grey-100 to-grey-50 hover:text-grey-900 hover:to-white hover:from-bg-grey-50'
							}
								`)}
							onClick={() => onChange(tab.id)}
							role="tab"
							aria-selected={tab.id === value}
						>
							<div
								className={ctl(`
									absolute inset-x-0 bottom-0 h-2
									bg-gradient-to-b from-grey-200/0 to-grey-200/30
									${tab.id === value && 'opacity-0'}
									${tab.id !== value && 'opacity-100'}
								`)}
							/>
							{tab.label}
						</button>
					))}

					<div className="grow-1" />

					{!isFloating && (
						<motion.div className="mb-1 mr-[1px]">
							<PopButton
								isFloating={isFloating}
								handleToggleFloating={handleToggleFloating}
								icon={<Glyphs.core.BalloonIcon />}
							/>
						</motion.div>
					)}
				</div>
				<div
					className="bg-white rounded-b-sm rounded-tr-sm ring ring-grey-100 overflow-hidden relative"
					role="tabpanel"
				>
					<AnimateChangeInHeight duration={0.2} animate={animateHeight}>
						<div>
							<AnimatePresence mode="popLayout">
								<motion.div
									key={value}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="overflow-auto max-h-[80vh] p-2"
								>
									{tabs.find((tab) => tab.id === value)?.children}
								</motion.div>
							</AnimatePresence>
						</div>
					</AnimateChangeInHeight>
				</div>
			</div>
		</motion.div>
	)
}

function PopButton(
	{ isFloating, handleToggleFloating, icon }: {
		isFloating: boolean
		handleToggleFloating: (e: React.PointerEvent<HTMLButtonElement>) => void
		icon: ReactNode
	},
) {
	return (
		<>
			<Tooltip content={isFloating ? 'Put me down' : 'Float this panel'} wrapperClassName="h-full">
				<button
					className="h-full px-2 py-2 rounded-sm cursor-pointer bg-grey-50 hover:bg-blue-100 hover:text-blue-500 transition-colors"
					onClick={handleToggleFloating}
				>
					{icon}
				</button>
			</Tooltip>
		</>
	)
}
